import type { ComponentType } from 'react';
import React, { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { useMutation } from '@apollo/react-hooks';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import Button from '@atlaskit/button/custom-theme-button';
import { default as NewButton } from '@atlaskit/button/new';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { N0, N10, N30, N300, B75, B400, G300, R400 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useFlags } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { token } from '@atlaskit/tokens';

import { ErrorDisplay } from '@confluence/error-boundary';
import { expValEquals } from '@confluence/feature-experiments';
import { SpaceIcon } from '@confluence/icons/entry-points/SpaceIcon';

import { FeedFollowRecommendationMutation } from './FeedFollowRecommendationMutation.graphql';
import type {
	FeedRecommendationMutation as FeedRecommendationMutationType,
	FeedRecommendationMutationVariables,
} from './__types__/FeedRecommendationMutation';

const i18n = defineMessages({
	follow: {
		id: 'feed.recommendation.tile.follow',
		defaultMessage: 'Follow',
		description: 'Button text to feed follow a recommended entity',
	},
	space: {
		id: 'feed.recommendation.tile.space',
		defaultMessage: 'Space',
		description: 'Label text for the space recommendation tile',
	},
	dismissLabel: {
		id: 'feed.recommendation.tile.dismiss.label',
		defaultMessage: 'Dismiss recommendation',
		description: 'Label text for the button to dismiss the recommendation',
	},
	flagFollowTitle: {
		id: 'feed.recommendation.flag.follow.success',
		defaultMessage: 'Followed {name}',
		description: 'Text to display for the title of the follow flag',
	},
	flagFollowAction: {
		id: 'feed.recommendation.flag.follow.action',
		defaultMessage: 'Dismiss',
		description: 'Text to display for the action of the follow flag to dismiss the flag',
	},
	flagErrorTitle: {
		id: 'feed.recommendation.flag.follow.error',
		defaultMessage: 'Could not follow {name}',
		description: 'Text to display for the title of the follow error flag',
	},
	flagErrorDescription: {
		id: 'feed.recommendation.flag.follow.error.description',
		defaultMessage: 'Please try again later',
		description: 'Text for the follow error flag description',
	},
});

export const TILE_MARGIN = 16;
export const PAGE_SIZE = 4;
const TILE_WIDTH_PERCENT = 100 / PAGE_SIZE;
const TILE_MARGIN_WIDTH = TILE_MARGIN * ((PAGE_SIZE - 1) / PAGE_SIZE);

const fullSizeButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			position: 'absolute',
			width: '100%',
			height: '100%',
		},
		spinnerStyles,
	};
};

const dismissTileButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			position: 'absolute',
			top: token('space.050', '4px'),
			right: token('space.050', '4px'),
			width: '28px',
			height: '28px',
		},
		spinnerStyles,
	};
};

const followButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			'&:hover': {
				backgroundColor: token('color.background.information.hovered', B75),
				color: `${token('color.text.brand', B400)} !important`,
			},
		},
		spinnerStyles,
	};
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const TileWrapper = styled.div<{ tileHover: boolean; load: boolean }>`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-evenly;
	height: 154px;
	flex-shrink: 0;
	min-width: 0;
	width: calc(${TILE_WIDTH_PERCENT}% - ${TILE_MARGIN_WIDTH}px);
	scroll-snap-align: start;
	background: ${token('color.background.input', N0)};
	border: 1px solid ${token('color.border', N30)};
	box-sizing: border-box;
	border-radius: 3px;
	padding: ${token('space.100', '8px')};
	text-align: center;
	pointer-events: auto;
	margin-right: ${TILE_MARGIN}px;
	&:hover {
		${({ tileHover }) =>
			tileHover ? `background: ${token('color.background.input.hovered', N10)}` : ``};
	}
	opacity: ${({ load }) => (load ? `0.5` : `1`)};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const UserIcon = styled.img`
	width: 32px;
	height: 32px;
	border-radius: 50%;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const NameAndTypeWrapper = styled.div`
	width: 100%;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const NameWrapper = styled.div`
	line-height: 20px;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
	position: relative;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const TitlePlaceholder = styled.div`
	height: 14px;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const TitleOrTypeWrapper = styled.div`
	color: ${token('color.text.subtle', N300)};
	font-size: 11px;
	line-height: 14px;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
	position: relative;
`;

type RecommendationTileProps = {
	name: string;
	id: string;
	url: string;
	icon: string;
	type: 'user' | 'space';
	title?: string;
	dismissTile: (id: string) => void;
	cardIndex: number;
	position: number;
};

export const RecommendationTile: ComponentType<RecommendationTileProps> = ({
	name,
	id,
	url,
	icon,
	type,
	title,
	dismissTile,
	cardIndex,
	position,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlags();
	const [tileHover, setTileHover] = useState(true);

	const enableTileHover = useCallback(() => {
		setTileHover(true);
	}, [setTileHover]);

	const disableTileHover = useCallback(() => {
		setTileHover(false);
	}, [setTileHover]);

	const onFollowSuccess = useCallback(() => {
		dismissTile(id);
		showFlag({
			title: <FormattedMessage {...i18n.flagFollowTitle} values={{ name }} />,
			icon: <SuccessIcon primaryColor={token('color.icon.success', G300)} label="Success" />,
			isAutoDismiss: true,
		});
	}, [dismissTile, id, name, showFlag]);

	const onFollowError = useCallback(() => {
		showFlag({
			title: <FormattedMessage {...i18n.flagErrorTitle} values={{ name }} />,
			description: <FormattedMessage {...i18n.flagErrorDescription} />,
			icon: <ErrorIcon label="error" primaryColor={token('color.icon.danger', R400)} />,
			isAutoDismiss: true,
		});
	}, [name, showFlag]);

	const [changeFeedFollows, { loading: followLoading, error: followError }] = useMutation<
		FeedRecommendationMutationType,
		FeedRecommendationMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		FeedFollowRecommendationMutation,
		{
			onCompleted: onFollowSuccess,
			onError: onFollowError,
			refetchQueries: ['FeedFollowQuery'],
		},
	);

	const [dismissRecTile, { loading: unfollowLoading, error: unfollowError }] = useMutation<
		FeedRecommendationMutationType,
		FeedRecommendationMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		FeedFollowRecommendationMutation,
	);

	const handleTileLinkButton = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'item',
				actionSubjectId: 'tile',
				source: 'suggestionsCard',
				attributes: {
					settingId: id,
					settingType: type,
					recommendationRank: cardIndex,
					pos: position,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, id, type, cardIndex, position]);

	const handleTileDismissButton = useCallback(async () => {
		void dismissRecTile({
			variables: type === 'user' ? { unfollowUser: [id] } : { unfollowSpace: [id] },
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismiss',
				source: 'suggestionsCard',
				attributes: {
					settingId: id,
					settingType: type,
					recommendationRank: cardIndex,
					pos: position,
				},
			},
		}).fire();
		dismissTile(id);
	}, [dismissTile, id, dismissRecTile, type, createAnalyticsEvent, cardIndex, position]);

	const handleFollowButton = useCallback(() => {
		void changeFeedFollows({
			variables: type === 'user' ? { followUser: [id] } : { followSpace: [id] },
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'follow',
				source: 'suggestionsCard',
				attributes: {
					settingId: id,
					settingType: type,
					recommendationRank: cardIndex,
					pos: position,
				},
			},
		}).fire();
	}, [changeFeedFollows, type, id, createAnalyticsEvent, cardIndex, position]);

	const titleOrType = type === 'user' ? title : intl.formatMessage(i18n.space);

	const generateFollowButtonBasedOnExperimentValue = () => {
		if (expValEquals('cc_smarts_follow_button_colour_change_experiment', 'cohort', 'primary')) {
			return (
				<NewButton
					onClick={handleFollowButton}
					onMouseEnter={disableTileHover}
					onMouseLeave={enableTileHover}
					appearance="primary"
					isDisabled={followLoading}
				>
					<FormattedMessage {...i18n.follow} />
				</NewButton>
			);
		} else if (
			expValEquals('cc_smarts_follow_button_colour_change_experiment', 'cohort', 'selected')
		) {
			return (
				<NewButton
					onClick={handleFollowButton}
					onMouseEnter={disableTileHover}
					onMouseLeave={enableTileHover}
					isDisabled={followLoading}
					isSelected
				>
					<FormattedMessage {...i18n.follow} />
				</NewButton>
			);
		} else {
			return (
				<Button
					onClick={handleFollowButton}
					onMouseEnter={disableTileHover}
					onMouseLeave={enableTileHover}
					theme={followButtonTheme}
					isDisabled={followLoading}
				>
					<FormattedMessage {...i18n.follow} />
				</Button>
			);
		}
	};

	return (
		<TileWrapper
			tileHover={tileHover}
			load={unfollowLoading || followLoading}
			data-testid="feed-rec-tile-wrapper"
		>
			{followError && <ErrorDisplay error={followError} />}
			{unfollowError && <ErrorDisplay error={unfollowError} />}
			<Button
				href={url}
				onClick={handleTileLinkButton}
				target="_blank"
				appearance="link"
				theme={fullSizeButtonTheme}
				testId="feed-rec-tile-link"
			/>
			<Button
				appearance="subtle"
				theme={dismissTileButtonTheme}
				onClick={handleTileDismissButton}
				onMouseEnter={disableTileHover}
				onMouseLeave={enableTileHover}
				spacing="none"
				iconBefore={<EditorCloseIcon label={intl.formatMessage(i18n.dismissLabel)} />}
				isDisabled={unfollowLoading}
			/>
			{/* eslint-disable-next-line styled-components-a11y/alt-text */}
			{type === 'user' ? <UserIcon src={icon} /> : <SpaceIcon src={icon} size="medium" />}
			<NameAndTypeWrapper>
				<Tooltip content={name}>
					<NameWrapper>{name}</NameWrapper>
				</Tooltip>
				<TitlePlaceholder>
					<Tooltip content={titleOrType}>
						<TitleOrTypeWrapper>{titleOrType}</TitleOrTypeWrapper>
					</Tooltip>
				</TitlePlaceholder>
			</NameAndTypeWrapper>
			{generateFollowButtonBasedOnExperimentValue()}
		</TileWrapper>
	);
};
